// Modèles d'occasion des faire-part et invitations animés (terme interne : « mini-site »).
// Un modèle fixe le VOCABULAIRE et le point de départ d'une page ; le moteur (MinisiteView.js),
// l'éditeur (MinisiteEditor.js) et le stockage (cartes_v2.params) sont les mêmes pour tous.
// L'occasion d'une page est `params.occasion`, posée à l'achat par la fiche produit
// (option_schema.defaults) et non modifiable ensuite. Absente = mariage (premières fiches).

import { useMemo } from 'react';
import { useT } from '../i18n';
import MODULES from './minisiteModules.json';

// Sections d'une page, dans l'ordre par défaut. `params.blocs` ne règle que l'ORDRE : une section
// s'affiche si elle a du contenu (texte, photo…) ou si son interrupteur est activé (réponses…).
export const MINISITE_BLOCS = [
  { key: 'texte', label: 'Votre message' },
  { key: 'photo', label: 'Votre photo' },
  { key: 'details', label: 'Informations' },
  { key: 'programme', label: 'Programme' },
  { key: 'compte_a_rebours', label: 'Compte à rebours' },
  { key: 'lieu', label: 'Lieu et plan' },
  { key: 'rsvp', label: 'Réponses des invités' },
  { key: 'cagnotte', label: 'Cagnotte / liste de cadeaux' },
];

const demoDate = (jours) => new Date(Date.now() + jours * 86400000).toISOString().slice(0, 10);
const LIEU_DEMO = { lieu: 'Domaine des Roses', adresse: '1200 route de Puyricard, 13100 Aix-en-Provence' };

export const MINISITE_OCCASIONS = {
  mariage: {
    hub: {
      path: '/faire-part-mariage-anime',
      evenement: 'mariage',
      seo: [
        { titre: "Faire-part de mariage animé ou site de mariage ?", texte: "Un site de mariage complet demande des heures de réglages pour un résultat que peu d'invités parcourent. Le faire-part de mariage animé va à l'essentiel : une invitation de mariage numérique qui s'ouvre en plein écran, donne la date, le lieu, le programme, et recueille les réponses. Vos invités n'ont rien à installer et aucun compte à créer." },
        { titre: "Comment envoyer son faire-part de mariage par WhatsApp ?", texte: "Après l'achat, vous recevez le lien de votre éditeur. Vous y saisissez vos prénoms, la date, le lieu et votre texte, puis vous copiez le lien de votre faire-part : il s'envoie par WhatsApp, SMS ou e-mail, avec un bel aperçu à vos prénoms. Un QR code permet aussi de le glisser dans un faire-part papier." },
      ],
      titre: 'Faire-part de mariage animé',
      metaTitle: 'Faire-part de mariage animé et digital avec RSVP',
      metaDescription: 'Faire-part de mariage digital animé : prénoms, date, lieu avec plan, programme et réponses des invités en ligne. À partager par WhatsApp, sans abonnement.',
      texte: 'Une invitation de mariage qui s\'ouvre sur le téléphone de vos invités : vos prénoms apparaissent en douceur, puis la date, le lieu et son plan, le programme de la journée, un compte à rebours — et leurs réponses vous arrivent dans un tableau, prêt à exporter pour le traiteur.',
      demo: { theme_visuel: 'tulipes-blanches.jpg', police: 'great-vibes', couleur: 'eucalyptus' },
    },
    label: 'Mariage',
    produit: 'Faire-part de mariage animé',
    intro: 'Nous nous marions',
    nomsLabel: 'Vos prénoms', nomsPlaceholder: 'Camille & Julien',
    sousTitrePlaceholder: '',
    dateLabel: 'Date du mariage',
    texteExemple: 'Nous avons la joie de vous convier à notre mariage. La cérémonie se déroulera à 15h…',
    compteARebours: 'Le grand jour dans',
    demo: {
      prenoms: 'Camille & Julien', date: demoDate(200), heure: '15:00', ...LIEU_DEMO,
      texte: 'Nous avons la joie de vous convier à notre mariage.\nLa cérémonie sera suivie d\'un vin d\'honneur et d\'une soirée dansante.',
      signature: 'Camille & Julien',
      programme: [{ heure: '15h00', titre: 'Cérémonie laïque' }, { heure: '17h00', titre: 'Vin d\'honneur' }, { heure: '20h00', titre: 'Dîner et soirée dansante' }],
      rsvp_actif: true, rsvp_accompagnants: true, rsvp_repas: true, rsvp_repas_options: 'Viande, Poisson, Végétarien', rsvp_allergies: true,
    },
  },
  'save-the-date': {
    hub: {
      path: '/save-the-date-anime',
      evenement: 'mariage',
      seo: [
        { titre: "Quand envoyer un save-the-date ?", texte: "Entre six et douze mois avant le mariage, dès que la date et la ville sont arrêtées — surtout si des invités viennent de loin ou si le mariage tombe en pleine saison. Le save-the-date numérique part en une minute et se met à jour si un détail change." },
        { titre: "Save-the-date digital : que mettre dedans ?", texte: "Vos prénoms, la date, la ville, et une phrase pour annoncer que le faire-part suivra. Le compte à rebours fait le reste. Inutile d'en dire plus : le save-the-date sert à bloquer la date dans les agendas." },
      ],
      titre: 'Save-the-date animé',
      metaTitle: 'Save-the-date animé à envoyer par WhatsApp',
      metaDescription: 'Save-the-date digital animé : vos prénoms, la date à retenir et un compte à rebours, sur le visuel de votre choix. Un lien à partager, modifiable à tout moment.',
      texte: 'Annoncez la date avant le faire-part : un lien à envoyer par WhatsApp ou SMS, vos prénoms qui s\'animent sur le fond de votre choix, la date, la ville et un compte à rebours. Simple, élégant, prêt en quelques minutes.',
      demo: { theme_visuel: 'fleurs-aquarelles-bleues.jpg', police: 'great-vibes', couleur: 'bleu-nuit' },
    },
    label: 'Save-the-date',
    produit: 'Save-the-date animé',
    intro: 'Réservez la date',
    nomsLabel: 'Vos prénoms', nomsPlaceholder: 'Camille & Julien',
    sousTitrePlaceholder: 'se marient',
    dateLabel: 'Date du mariage',
    texteExemple: 'Notez bien la date ! Le faire-part suivra dans quelques semaines.',
    compteARebours: 'Rendez-vous dans',
    demo: {
      prenoms: 'Camille & Julien', sous_titre: 'se marient', date: demoDate(300), lieu: 'Aix-en-Provence',
      texte: 'Notez bien la date !\nLe faire-part suivra dans quelques semaines.', carte_lieu: false,
    },
  },
  anniversaire: {
    hub: {
      path: '/invitation-anniversaire-animee',
      evenement: 'anniversaire',
      seo: [
        { titre: "Une invitation d'anniversaire pour 30, 40 ou 50 ans", texte: "Les anniversaires à chiffre rond rassemblent souvent trente à quatre-vingts personnes : amis, famille, collègues. L'invitation d'anniversaire animée leur donne la date, l'adresse avec son plan, le thème ou la tenue, et un formulaire pour répondre. Vous savez combien vous serez, sans tenir de liste à la main." },
        { titre: "Anniversaire surprise : comment inviter discrètement ?", texte: "Envoyez le lien en message privé plutôt que dans un groupe, et précisez dans votre texte que c'est une surprise. En répondant, chaque invité peut laisser un petit mot — une anecdote, une chanson pour la playlist — et vous retrouvez tout dans le tableau des réponses." },
      ],
      titre: 'Invitation d\'anniversaire animée',
      metaTitle: 'Invitation d\'anniversaire animée, 30, 40, 50 ans',
      metaDescription: 'Invitation d\'anniversaire digitale et animée pour adulte : date, lieu avec plan, programme et réponses des invités en ligne. À envoyer par WhatsApp.',
      texte: 'Dix-huit, trente, quarante, cinquante ans : une invitation qui donne le ton de la fête. Le prénom s\'anime sur le fond choisi, vos invités trouvent la date, l\'adresse avec son plan, le thème de la soirée, et répondent en ligne — vous savez enfin combien vous serez.',
      demo: { theme_visuel: 'feuillor-fond-noir.jpg', police: 'great-vibes', couleur: 'or', ambiance: 'sombre', voile: 40 },
    },
    label: 'Anniversaire',
    produit: 'Invitation d\'anniversaire animée',
    intro: 'Vous êtes invités',
    nomsLabel: 'Prénom à l\'honneur', nomsPlaceholder: 'Sophie',
    sousTitrePlaceholder: 'fête ses 40 ans',
    dateLabel: 'Date de la fête',
    texteExemple: 'Quarante ans, ça se fête ! Rejoignez-nous pour une soirée entre amis…',
    compteARebours: 'La fête commence dans',
    demo: {
      prenoms: 'Sophie', sous_titre: 'fête ses 40 ans', date: demoDate(60), heure: '19:30', lieu: 'La Guinguette du Lac', adresse: '12 chemin du Lac, 74000 Annecy',
      texte: 'Quarante ans, ça se fête !\nRejoignez-nous pour une soirée entre amis : buffet, musique et quelques surprises.',
      details: [{ label: 'Tenue', valeur: 'Chic et décontractée' }, { label: 'Thème', valeur: 'Années 80' }],
      rsvp_actif: true, rsvp_accompagnants: true, rsvp_questions: ['Une chanson pour la playlist ?'],
    },
  },
  'anniversaire-enfant': {
    hub: {
      path: '/invitation-anniversaire-enfant-animee',
      evenement: 'anniversaire',
      seo: [
        { titre: "Une invitation d'anniversaire enfant avec son thème préféré", texte: "Foot, licorne, dinosaures, pirates, princesse : plutôt que de chercher le carton qui correspond, envoyez votre propre image de fond dans l'éditeur. Ajoutez la photo de votre enfant, son âge, l'adresse, l'heure de début et de fin. L'invitation numérique part aux parents par WhatsApp." },
        { titre: "Savoir qui vient, et qui est allergique", texte: "Les parents répondent en un clic et signalent les allergies alimentaires : plus de cartons-réponse oubliés dans les cartables, ni de gâteau à refaire au dernier moment." },
      ],
      titre: 'Invitation d\'anniversaire animée pour enfant',
      metaTitle: 'Invitation d\'anniversaire enfant animée',
      metaDescription: 'Invitation d\'anniversaire digitale pour enfant : votre propre fond (foot, licorne, héros), sa photo, l\'adresse, les horaires. Les parents répondent en ligne.',
      texte: 'Fini les cartons perdus au fond du cartable. Une invitation à envoyer aux parents par WhatsApp, avec le fond que votre enfant adore — vous pouvez envoyer votre propre image —, sa photo, l\'adresse avec son plan, l\'heure de début et de fin, et les réponses (allergies comprises) dans un tableau.',
      demo: { theme_visuel: 'fleur-ronde-rose.jpeg', police: 'neucha', couleur: 'bleu-nuit' },
    },
    label: 'Anniversaire d\'enfant',
    produit: 'Invitation d\'anniversaire animée pour enfant',
    intro: 'Tu es invité(e) !',
    nomsLabel: 'Prénom de l\'enfant', nomsPlaceholder: 'Léo',
    sousTitrePlaceholder: 'fête ses 7 ans',
    dateLabel: 'Date de la fête',
    texteExemple: 'Viens fêter mon anniversaire ! Au programme : jeux, gâteau et chasse au trésor…',
    compteARebours: 'La fête commence dans',
    demo: {
      prenoms: 'Léo', sous_titre: 'fête ses 7 ans', date: demoDate(30), heure: '14:00', lieu: 'À la maison', adresse: '8 rue des Lilas, 69003 Lyon',
      texte: 'Viens fêter mon anniversaire !\nAu programme : jeux, gâteau et chasse au trésor.',
      details: [{ label: 'Fin de la fête', valeur: '17h30' }, { label: 'À prévoir', valeur: 'Des baskets' }],
      rsvp_actif: true, rsvp_allergies: true, rsvp_message_invite: false,
    },
  },
  bapteme: {
    hub: {
      path: '/invitation-bapteme-animee',
      evenement: 'bapteme-communion',
      seo: [
        { titre: "Que mettre sur une invitation de baptême ?", texte: "Le prénom de l'enfant, la date et l'heure de la cérémonie, l'adresse de l'église, le lieu du repas, le nom du parrain et de la marraine, et une date limite de réponse. L'invitation de baptême numérique détaille la cérémonie et le repas dans le programme, affiche le plan du lieu principal et compte adultes et enfants séparément." },
        { titre: "Baptême civil ou religieux", texte: "Le modèle convient aux deux : changez simplement la phrase d'introduction et le programme. Pour un baptême républicain, indiquez la mairie à la place de l'église." },
      ],
      titre: 'Invitation de baptême animée',
      metaTitle: 'Invitation de baptême animée et numérique',
      metaDescription: 'Invitation de baptême digitale animée : prénom, date, église et repas avec le plan, parrain et marraine, et les réponses de la famille en ligne.',
      texte: 'Le prénom de votre enfant apparaît en douceur, puis la date, l\'église, le lieu du déjeuner avec son plan, le nom du parrain et de la marraine. La famille répond en ligne, adultes et enfants comptés à part : de quoi réserver le restaurant sans relancer tout le monde.',
      demo: { theme_visuel: 'eucalyptus-or.jpg', police: 'great-vibes', couleur: 'eucalyptus' },
    },
    label: 'Baptême',
    produit: 'Invitation de baptême animée',
    intro: 'Baptême de',
    nomsLabel: 'Prénom de l\'enfant', nomsPlaceholder: 'Louise',
    sousTitrePlaceholder: '',
    dateLabel: 'Date du baptême',
    texteExemple: 'Nous serions heureux de vous compter parmi nous pour le baptême de Louise…',
    compteARebours: 'Rendez-vous dans',
    demo: {
      prenoms: 'Louise', date: demoDate(90), heure: '11:00', lieu: 'Église Saint-Jean', adresse: 'Place de l\'Église, 13100 Aix-en-Provence',
      texte: 'Nous serions heureux de vous compter parmi nous pour le baptême de Louise.\nLa cérémonie sera suivie d\'un déjeuner en famille.',
      details: [{ label: 'Marraine', valeur: 'Claire' }, { label: 'Parrain', valeur: 'Antoine' }],
      programme: [{ heure: '11h00', titre: 'Cérémonie à l\'église' }, { heure: '12h30', titre: 'Déjeuner' }],
      rsvp_actif: true, rsvp_accompagnants: true, rsvp_enfants: true,
    },
  },
  communion: {
    hub: {
      path: '/invitation-communion-animee',
      evenement: 'bapteme-communion',
      seo: [
        { titre: "Première communion, profession de foi, confirmation", texte: "La même invitation de communion numérique s'adapte aux trois : vous modifiez la phrase d'introduction (« Première communion de », « Profession de foi de », « Confirmation de »), le programme et les lieux. Les familles répondent en ligne, ce qui simplifie la réservation du repas." },
        { titre: "Une invitation de communion à envoyer à toute la famille", texte: "Oncles, tantes, parrains, cousins : un seul lien à partager, lisible sur tous les téléphones, avec un aperçu au prénom de l'enfant. Le QR code permet de l'imprimer sur un carton pour les grands-parents." },
      ],
      titre: 'Invitation de communion animée',
      metaTitle: 'Invitation de communion animée et numérique',
      metaDescription: 'Invitation de communion digitale animée : prénom, date, messe et repas au programme, lieu avec son plan et réponses des invités en ligne.',
      texte: 'Première communion, profession de foi ou confirmation : une invitation sobre et lumineuse, à envoyer à toute la famille. La messe et le déjeuner dans le programme, le lieu avec son plan d\'accès — et les réponses de chacun, réunies dans un tableau.',
      demo: { theme_visuel: 'feuillage-dore-blanc.jpg', police: 'cormorant', couleur: 'or' },
    },
    label: 'Communion',
    produit: 'Invitation de communion animée',
    intro: 'Première communion de',
    nomsLabel: 'Prénom de l\'enfant', nomsPlaceholder: 'Arthur',
    sousTitrePlaceholder: '',
    dateLabel: 'Date de la communion',
    texteExemple: 'Arthur fera sa première communion. Nous serions heureux de partager ce moment avec vous…',
    compteARebours: 'Rendez-vous dans',
    demo: {
      prenoms: 'Arthur', date: demoDate(120), heure: '10:30', lieu: 'Église Notre-Dame', adresse: '3 rue de la Paroisse, 78000 Versailles',
      texte: 'Arthur fera sa première communion.\nNous serions heureux de partager ce moment avec vous, puis de vous retrouver pour le déjeuner.',
      programme: [{ heure: '10h30', titre: 'Messe' }, { heure: '12h30', titre: 'Déjeuner' }],
      rsvp_actif: true, rsvp_accompagnants: true, rsvp_enfants: true,
    },
  },
  naissance: {
    hub: {
      path: '/faire-part-naissance-anime',
      evenement: 'naissance',
      seo: [
        { titre: "Un faire-part de naissance digital avec la photo de bébé", texte: "Vous envoyez la photo depuis votre téléphone : elle est recadrée en rond, en arche ou en carré, au-dessus du prénom qui s'anime. Ajoutez la date de naissance, le poids, la taille, l'heure, quelques mots et votre signature. Le faire-part de naissance numérique se partage par WhatsApp, SMS ou e-mail." },
        { titre: "Préparer le faire-part avant la naissance", texte: "Choisissez le modèle et rédigez votre texte pendant la grossesse : le jour J, il ne reste qu'à saisir le prénom, les mensurations et à ajouter la photo. Vous pouvez la remplacer plus tard sans changer le lien déjà envoyé." },
      ],
      titre: 'Faire-part de naissance animé',
      metaTitle: 'Faire-part de naissance animé avec photo de bébé',
      metaDescription: 'Faire-part de naissance digital animé : la photo de bébé, son prénom, sa date de naissance, son poids et sa taille. Un lien à partager par WhatsApp.',
      texte: 'La photo de votre bébé, son prénom qui s\'anime, sa date de naissance, son poids, sa taille et vos quelques mots : le faire-part que l\'on envoie depuis la maternité, par WhatsApp ou SMS, à toute la famille. Vous changez la photo quand vous voulez, le lien reste le même.',
      demo: { theme_visuel: 'naissance-nuages-etoiles.jpg', police: 'allura', couleur: 'or', taille_prenoms: 125 },
    },
    label: 'Naissance',
    produit: 'Faire-part de naissance animé',
    intro: 'Elle est née !',
    nomsLabel: 'Prénom du bébé', nomsPlaceholder: 'Jade',
    sousTitrePlaceholder: '',
    dateLabel: 'Date de naissance',
    texteExemple: 'Après neuf mois d\'attente, notre petite Jade a pointé le bout de son nez…',
    compteARebours: '',
    demo: {
      prenoms: 'Jade', intro: 'Elle est née !', date: new Date(Date.now() - 10 * 86400000).toISOString().slice(0, 10),
      texte: 'Après neuf mois d\'attente, notre petite Jade a pointé le bout de son nez.\nToute la famille se porte à merveille.',
      signature: 'Marie & Thomas',
      details: [{ label: 'Poids', valeur: '3,250 kg' }, { label: 'Taille', valeur: '50 cm' }, { label: 'Heure', valeur: '14h32' }],
      compte_a_rebours: false,
    },
  },
};

// Ordre d'affichage des occasions (accueil, page « Faire-part et invitations animés »).
export const MINISITE_OCCASION_KEYS = ['mariage', 'save-the-date', 'naissance', 'bapteme', 'communion', 'anniversaire', 'anniversaire-enfant'];
export const MINISITE_HUB_PATH = '/faire-part-et-invitations-animes';

// Vidéo de démonstration de l'éditeur, commune à toutes les fiches (à fournir par le propriétaire :
// chemin d'un fichier .mp4 déposé dans public/, ex. '/assets_react/videos/demo-editeur.mp4').
// Vide = le bloc vidéo n'est pas affiché.
export const MINISITE_VIDEO_URL = '';

// Arguments affichés sur les pages d'occasion et les fiches produit.
export const MINISITE_ARGUMENTS = [
  'Prêt en quelques minutes, depuis votre téléphone',
  'Sans Canva, sans compte à créer, sans abonnement',
  'Invités illimités, paiement unique',
  'Modifiable à tout moment, le lien ne change pas',
  'En ligne pendant 24 mois',
  'Aucune publicité pour vos invités',
];

export const getOccasion = (params) => MINISITE_OCCASIONS[params?.occasion] || MINISITE_OCCASIONS.mariage;

// Modules = ce que contient un produit (minisiteModules.json, source unique). À l'achat, la liste
// du produit est recopiée dans la page (`params.modules`, non modifiable par le client, vérifiée
// par le serveur) : l'éditeur et la page n'ouvrent que ce qui a été acheté. Une page sans liste
// (créée avant cette règle) suit la grille actuelle de son occasion.
export const MINISITE_MODULES = MODULES.modules;
export const MINISITE_SOCLE = MODULES.socle;
export const modulesOfOccasion = (occasionKey) => MODULES.occasions[occasionKey] || MODULES.occasions.mariage;
export const modulesOf = (params) => (Array.isArray(params?.modules)
  ? params.modules
  : modulesOfOccasion(params?.occasion || 'mariage'));
export const hasModule = (params, key) => modulesOf(params).includes(key);

// Ordre effectif des sections : celui choisi par le client, complété des sections qu'il ne connaît
// pas encore (pages créées avant l'ajout d'une section), limité aux modules achetés.
export const resolveBlocs = (params) => {
  const tous = MINISITE_BLOCS.map((b) => b.key).filter((k) => hasModule(params, k));
  const choisis = Array.isArray(params?.blocs) ? params.blocs.filter((k) => tous.includes(k)) : [];
  return [...choisis, ...tous.filter((k) => !choisis.includes(k))];
};

// ─── Traductions (lot « faire-part animés : catalogue ») ─────────────────────────────────────────────
// Les exports ci-dessus restent FRANÇAIS et inchangés (le moteur, la fabrique `minisite-factory/build.py` et
// les fiches en base les lisent tels quels). Pour AFFICHER du texte, les composants utilisent les hooks
// ci-dessous : mêmes formes que les objets français, chaînes traduites, repli sur le français.
// Les traductions vivent dans src/i18n/locales/<langue>/minisite-occasions.json, indexées par clé
// (occasions.<occasion>.…, modules.<clé>.…, blocs.<clé>, socle.<clé>, arguments.<clé>, theme.… ) — jamais dans
// minisiteModules.json. Le français y est une copie de contrôle (test : minisiteOccasions.test.js).

// Traduction d'une chaîne dont le français vit dans ce fichier ou dans minisiteModules.json.
// Texte vide (placeholder volontairement vide) = laissé vide. Clé absente = français.
const tr = (t, path, fr) => (fr ? t.or(`minisite-occasions.${path}`, fr) : fr);

// Liste de textes → clés numérotées (p1, p2… ou b1, b2…) : un objet à clés nommées dans le dictionnaire.
const trItems = (t, path, prefix, items, fields) => items.map((item, i) => {
  const out = { ...item };
  fields.forEach((f) => { out[f] = tr(t, `${path}.${prefix}${i + 1}.${f}`, item[f]); });
  return out;
});

const translateOccasion = (t, key, o) => {
  const p = `occasions.${key}`;
  const demo = { ...o.demo };
  ['sous_titre', 'lieu', 'texte', 'intro', 'rsvp_repas_options'].forEach((f) => { if (demo[f]) demo[f] = tr(t, `${p}.demo.${f}`, demo[f]); });
  if (demo.programme) demo.programme = trItems(t, `${p}.demo.programme`, 'p', demo.programme, ['heure', 'titre']);
  if (demo.details) demo.details = trItems(t, `${p}.demo.details`, 'd', demo.details, ['label', 'valeur']);
  if (demo.rsvp_questions) demo.rsvp_questions = demo.rsvp_questions.map((q, i) => tr(t, `${p}.demo.rsvp_questions.q${i + 1}`, q));
  return {
    ...o,
    label: tr(t, `${p}.label`, o.label),
    produit: tr(t, `${p}.produit`, o.produit),
    intro: tr(t, `${p}.intro`, o.intro),
    nomsLabel: tr(t, `${p}.nomsLabel`, o.nomsLabel),
    nomsPlaceholder: tr(t, `${p}.nomsPlaceholder`, o.nomsPlaceholder),
    sousTitrePlaceholder: tr(t, `${p}.sousTitrePlaceholder`, o.sousTitrePlaceholder),
    dateLabel: tr(t, `${p}.dateLabel`, o.dateLabel),
    texteExemple: tr(t, `${p}.texteExemple`, o.texteExemple),
    compteARebours: tr(t, `${p}.compteARebours`, o.compteARebours),
    // Titre de l'encadré « Ce que contient… » (phrase complète : l'ordre des mots et la casse changent selon la langue).
    contenuTitre: tr(t, `${p}.contenuTitre`, `Ce que contient votre ${o.produit.toLowerCase()}`),
    demo,
    hub: {
      ...o.hub,
      titre: tr(t, `${p}.hub.titre`, o.hub.titre),
      metaTitle: tr(t, `${p}.hub.metaTitle`, o.hub.metaTitle),
      metaDescription: tr(t, `${p}.hub.metaDescription`, o.hub.metaDescription),
      texte: tr(t, `${p}.hub.texte`, o.hub.texte),
      seo: trItems(t, `${p}.hub.seo`, 'b', o.hub.seo || [], ['titre', 'texte']),
    },
  };
};

/** Toutes les occasions traduites : { mariage: {…}, 'save-the-date': {…}, … } (même forme que MINISITE_OCCASIONS). */
export const useMinisiteOccasions = () => {
  const t = useT('minisite-occasions');
  return useMemo(
    () => Object.fromEntries(Object.entries(MINISITE_OCCASIONS).map(([key, o]) => [key, translateOccasion(t, key, o)])),
    [t],
  );
};

/** Une occasion traduite par sa clé ('mariage', 'bapteme'…) ; clé inconnue = mariage, comme getOccasion. */
export const useOccasionByKey = (key) => {
  const all = useMinisiteOccasions();
  return all[key] || all.mariage;
};

/** Comme getOccasion(params), tout le vocabulaire traduit (libellés, exemples, démo, textes de la page d'occasion). */
export const useOccasion = (params) => useOccasionByKey(params?.occasion);

// Lignes d'âge écrites en français par minisite-factory/build.py (champ sous_titre des fiches).
const SOUS_TITRES_FICHE = [
  [/^fête ses (\d+) ans$/, 'ans'],
  [/^fêtent leurs (\d+) ans$/, 'ansPluriel'],
  [/^fête son 1er anniversaire$/, 'premier'],
  [/^se marient$/, 'seMarient'],
  [/^se disent oui$/, 'seDisentOui'],
];

// Phrases d'introduction réglées dans certaines fiches (defaults.intro), écrites en français en base.
const INTROS_FICHE = {
  'Il est né !': 'ilEstNe', 'Ça se fête !': 'caSeFete', Bienvenue: 'bienvenue', 'Vous êtes invités': 'vousEtesInvites',
  'Soirée pyjama !': 'soireePyjama', 'Profession de foi de': 'professionDeFoi', 'Confirmation de': 'confirmation',
};

/**
 * Réglages de départ d'une FICHE (option_schema.defaults) dans la langue de la page : seule la phrase
 * d'introduction est du texte. Elle sert à l'aperçu et de valeur de départ à la page achetée (l'acheteur la
 * modifie ensuite librement). En français, ou pour une phrase inconnue : les réglages de la base, tels quels.
 */
export const useFicheDefaults = () => {
  const t = useT('minisite-occasions');
  return useMemo(() => (defaults) => {
    const cle = defaults && INTROS_FICHE[defaults.intro];
    return cle ? { ...defaults, intro: t.or(`minisite-occasions.ficheDemo.intro.${cle}`, defaults.intro) } : defaults;
  }, [t]);
};

/**
 * Exemple propre à une FICHE produit (option_schema.demo : prénom du modèle, ligne d'âge, texte), écrit en
 * français en base par minisite-factory/build.py. Renvoie `(cléOccasion, demo) => demo` traduit : le texte
 * de l'occasion avec le prénom du modèle, la ligne d'âge reconnue par sa forme. En français, ou pour une
 * forme inconnue : l'exemple de la base, tel quel.
 */
export const useFicheDemo = () => {
  const t = useT('minisite-occasions');
  return useMemo(() => (occasionKey, demo) => {
    if (!demo) return demo;
    const out = { ...demo };
    if (out.texte) out.texte = t.or(`minisite-occasions.ficheDemo.texte.${occasionKey}`, out.texte, { nom: out.prenoms || '' });
    if (out.sous_titre) {
      const forme = SOUS_TITRES_FICHE.find(([re]) => re.test(out.sous_titre));
      if (forme) out.sous_titre = t.or(`minisite-occasions.ficheDemo.sousTitre.${forme[1]}`, out.sous_titre, { n: (out.sous_titre.match(forme[0]) || [])[1] });
    }
    return out;
  }, [t]);
};

/** MINISITE_MODULES traduits : [{ key, label, detail }] (même ordre). */
export const useMinisiteModules = () => {
  const t = useT('minisite-occasions');
  return useMemo(() => MINISITE_MODULES.map((m) => ({
    ...m,
    label: tr(t, `modules.${m.key}.label`, m.label),
    detail: tr(t, `modules.${m.key}.detail`, m.detail),
  })), [t]);
};

// Clés des lignes du socle et des arguments (l'ordre est celui de minisiteModules.json / MINISITE_ARGUMENTS ;
// une ligne ajoutée plus tard sans clé ici reste en français jusqu'à ce qu'on lui en donne une).
const SOCLE_KEYS = ['prenoms', 'personnalisation', 'date', 'lien', 'modifiable'];
const ARGUMENT_KEYS = ['rapide', 'sansCompte', 'illimites', 'modifiable', 'vingtQuatreMois', 'sansPub'];

/** Le socle commun à tous les modèles, traduit : tableau de chaînes (même ordre que MINISITE_SOCLE). */
export const useMinisiteSocle = () => {
  const t = useT('minisite-occasions');
  return useMemo(() => MINISITE_SOCLE.map((ligne, i) => tr(t, `socle.${SOCLE_KEYS[i] || `s${i + 1}`}`, ligne)), [t]);
};

/** MINISITE_ARGUMENTS traduits : tableau de chaînes (même ordre). */
export const useMinisiteArguments = () => {
  const t = useT('minisite-occasions');
  return useMemo(() => MINISITE_ARGUMENTS.map((a, i) => tr(t, `arguments.${ARGUMENT_KEYS[i] || `a${i + 1}`}`, a)), [t]);
};

/** Libellés des sections (MINISITE_BLOCS) par clé : { texte: 'Votre message', photo: 'Votre photo', … }. */
export const useBlocLabels = () => {
  const t = useT('minisite-occasions');
  return useMemo(() => Object.fromEntries(MINISITE_BLOCS.map((b) => [b.key, tr(t, `blocs.${b.key}`, b.label)])), [t]);
};
